
import { IGroupDividerProps, IGroup } from 'office-ui-fabric-react/lib/GroupedList';
import { autobind, IComponentAsProps } from 'office-ui-fabric-react/lib/Utilities';
import * as React from 'react';
import { ItemDefaultDropZone, renderChildrenWithDropZoneElement } from '../defaultDropZone/DefaultDropZone';
import { IActionDropZoneStateProps } from '../decorators/asActionDropZone';
import { Selection } from 'office-ui-fabric-react/lib/Selection';
import { GROUP_ITEM_LIMIT } from '../../facets/GroupFacets';
import { ItemDefaultClickAction } from '../actions/defaultClick/DefaultClickAction';
import { RegisterInvokeAction } from '../decorators/withActionInvoker';
import { IActionStateProps, WithAction, evaluateAction } from '../withAction/WithAction';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';

/**
 * @public
 */
export interface IGroupItemDetailsHeaderProps extends IGroupDividerProps {
  itemKey: string;
  itemSetKey: string;
  selection?: Selection;
}

interface IInteractiveGroupHeaderProps extends IGroupDividerProps {
  hasDropFinished: boolean;
  selection?: Selection;
}

class InteractiveGroupHeader extends React.PureComponent<IComponentAsProps<IInteractiveGroupHeaderProps>> {
  public componentDidUpdate(prevProps: Readonly<IInteractiveGroupHeaderProps>): void {
    const {
      hasDropFinished,
      ...groupHeaderProps
    } = this.props;

    if (!prevProps.hasDropFinished && hasDropFinished) {
      if (groupHeaderProps.onToggleCollapse && groupHeaderProps.group) {
        if (groupHeaderProps.group.isCollapsed) {
          groupHeaderProps.onToggleCollapse(groupHeaderProps.group);
        }
      }
    }
  }

  public render(): JSX.Element {
    const {
      defaultRender: GroupHeader,
      ...groupHeaderProps
    } = this.props;

    return (
      <div onKeyDown={ this._onKeyDown } onKeyUp={ this._onKeyUp }>
        { GroupHeader ?
          <GroupHeader
            { ...groupHeaderProps }
            onToggleSelectGroup={ this._onToggleSelectGroup }
          /> : null
        }
      </div>
    );
  }

  @autobind
  private _onKeyDown(event: React.KeyboardEvent<HTMLElement>): void {
    const {
      group,
      isCollapsedGroupSelectVisible
    } = this.props;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
    } else if (event.key === ' ' && group) {
      if (isCollapsedGroupSelectVisible || !group.isCollapsed) {
        this._onToggleSelectGroup(group);
        event.preventDefault();
      }
    }
  }

  @autobind
  private _onKeyUp(event: React.KeyboardEvent<HTMLElement>): void {
    const {
      group,
      onToggleCollapse,
      onGroupHeaderClick
    } = this.props;

    if (group) {
      if (event.key === 'ArrowLeft' && onToggleCollapse) {
        // Say group is expanded so we collapse it
        const expandedGroup = { ...group, isCollapsed: false };
        onToggleCollapse(expandedGroup);
      } else if (event.key === 'ArrowRight' && onToggleCollapse) {
        // Say group is collapsed so we expand it
        const collapsedGroup = { ...group, isCollapsed: true };
        onToggleCollapse(collapsedGroup);
      } else if (event.key === 'Enter' && onGroupHeaderClick) {
        onGroupHeaderClick(group);
      }
    }
  }

  @autobind
  private _onToggleSelectGroup(group: IGroup): void {
    const {
      selection
    } = this.props;

    if (selection) {
      if (!group.children) {
        const count = group.count > GROUP_ITEM_LIMIT ? GROUP_ITEM_LIMIT : group.count;
        selection.toggleRangeSelected(group.startIndex, count);
      } else {
        let isGroupSelected = true;
        for (let childGroup of group.children) {
          if (!childGroup.isCollapsed) {
            const childCount = childGroup.count > GROUP_ITEM_LIMIT ? GROUP_ITEM_LIMIT : childGroup.count;
            const isChildGroupSelected = selection.isRangeSelected(childGroup.startIndex, childCount);
            isGroupSelected = isGroupSelected && isChildGroupSelected;
          }
        }
        const newSelectionState = !isGroupSelected;
        for (let childGroup of group.children) {
          if (!childGroup.isCollapsed) {
            const childCount = childGroup.count > GROUP_ITEM_LIMIT ? GROUP_ITEM_LIMIT : childGroup.count;
            const isChildGroupSelected = selection.isRangeSelected(childGroup.startIndex, childCount);
            if (newSelectionState !== isChildGroupSelected) {
              selection.toggleRangeSelected(childGroup.startIndex, childCount);
            }
          }
        }
      }
    }
  }
}

/**
 * @public
 */
export class GroupItemDetailsHeader extends React.PureComponent<IComponentAsProps<IGroupItemDetailsHeaderProps>> {
  public render(): JSX.Element {
    const {
      itemKey,
      defaultRender: GroupItemHeader,
      itemSetKey,
      ...headerProps
    } = this.props;

    return (
      <WithItemCache>
        { ({ itemCache }: IItemCacheStateProps) => {
          const defaultClickAction = evaluateAction(itemCache, (
            <ItemDefaultClickAction itemKey={ itemKey } />
          ));

          const dragDropElement = evaluateAction(itemCache, (
            <ItemDefaultDropZone itemKey={ itemKey } />
          ));

          return (
            <WithAction action={ defaultClickAction }>
              { (invokeActionState: IActionStateProps) => {
                return renderChildrenWithDropZoneElement((
                  { hasDropFinished }: IActionDropZoneStateProps
                ) => {
                  return (
                    <>
                      <RegisterInvokeAction
                        { ...invokeActionState }
                        itemKey={ itemKey }
                      />
                      {
                        GroupItemHeader ? (
                          <GroupItemHeader
                            itemKey={ itemKey }
                            itemSetKey={ itemSetKey }
                            { ...headerProps }
                          >
                            <InteractiveGroupHeader
                              hasDropFinished={ hasDropFinished }
                              { ...headerProps }
                            />
                          </GroupItemHeader>
                        ) : null
                      }
                    </>
                  );
                }, dragDropElement);
              } }
            </WithAction>
          );
        } }
      </WithItemCache>
    );
  }
}
